<!-- Pending file attachment preview tile in the chat composer, with upload progress and removal. -->

<script lang="ts" setup>
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { TransitionFade } from '@nzyme/vue-transitions/TransitionFade.jsx';
import { X } from 'lucide-vue-next';
import { computed } from 'vue';

import FileExtension from '../files/FileExtension.vue';
import { parseFileName } from '../utils/parseFileName.js';
import * as l from './ChatInputAttachment.loc.js';
import ChatInputAttachmentProgress from './ChatInputAttachmentProgress.vue';

interface Events {
  delete: [];
}

interface Props {
  fileName: string;
  fileType: string;
  previewUrl?: string;
  width?: number;
  height?: number;
  progress?: number;
  error?: string;
  removable?: boolean;
}

const props = defineProps<Props>();

defineEmits<Events>();

const t = useTranslate();

const isImage = computed(() => {
  return props.fileType.startsWith('image/');
});

const aspectRatio = computed(() => {
  if (props.width && props.height) {
    return props.width / props.height;
  }
  return undefined;
});

const parsedFileName = computed(() => parseFileName(props.fileName));
</script>

<template>
  <div
    :class="[css.attachment, isImage && css.attachment_image]"
    :style="aspectRatio ? { aspectRatio: aspectRatio.toString() } : undefined"
  >
    <img
      v-if="isImage && previewUrl"
      :src="previewUrl"
      :alt="fileName"
      :class="css.previewImage"
    />

    <div
      v-else
      :class="[css.filePreview, error && css.filePreview_error]"
    >
      <FileExtension
        v-if="parsedFileName.extension"
        :extension="parsedFileName.extension"
        :class="css.fileExtensionWrapper"
      />
      <div :class="css.fileName">{{ parsedFileName.name }}</div>
    </div>

    <TransitionFade :duration="200">
      <div
        v-if="!error && progress !== undefined"
        :class="css.uploadingOverlay"
      >
        <ChatInputAttachmentProgress :progress="progress" />
      </div>
    </TransitionFade>

    <TransitionFade :duration="200">
      <div
        v-if="error"
        :class="[css.errorFooter, isImage && css.errorFooter_image]"
      >
        <div :class="css.errorMessage">{{ error }}</div>
      </div>
    </TransitionFade>

    <button
      v-if="removable ?? true"
      :class="[css.deleteButton, error && css.deleteButton_error]"
      :title="t(l.remove)"
      type="button"
      @click="$emit('delete')"
    >
      <div :class="css.deleteButtonInner">
        <X :class="css.deleteButtonIcon" />
      </div>
    </button>
  </div>
</template>

<style lang="scss" module="css">
.attachment {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: $border-radius-08;
  overflow: hidden;
  background: $color-gray-50;
  flex: 0 0 auto;
  min-width: 120px;
  max-width: 180px;

  &_image {
    max-width: 300px;
  }
}

.previewImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.filePreview {
  --file-name-color: inherit;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: $spacing-12;
  flex: 1;
  min-height: 0;
  background: inherit;

  &_error {
    --file-extension-color: #{$color-red-600};
    --file-extension-label-color: #{$color-red-700};
    --file-name-color: #{$color-red-700};

    background: $color-red-50;
  }
}

.fileExtensionWrapper {
  margin-left: -1 * $spacing-04;
}

.fileName {
  width: 100%;
  font-size: $font-size-14;
  margin-top: auto;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-clamp: 3;
  box-orient: vertical;
  color: var(--file-name-color);
}

.uploadingOverlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba($color-gray-50, 0.7);
  backdrop-filter: blur(5px);
  border-radius: $border-radius-08;
}

.errorFooter {
  display: flex;
  align-items: center;
  justify-content: center;
  background: $color-red-600;
  padding: $spacing-08 $spacing-12;
  flex-shrink: 0;

  &_image {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }
}

.errorMessage {
  font-size: $font-size-12;
  color: $color-white;
  text-align: center;
  font-weight: 500;
  line-height: 1.3;
}

.deleteButton {
  position: absolute;
  top: $spacing-08;
  right: $spacing-08;
  margin: -1 * $spacing-12;
  padding: $spacing-12;
  cursor: pointer;

  &_error {
    .deleteButtonInner {
      background: $color-white;
      color: $color-red-600;
    }
  }
}

.deleteButtonInner {
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  color: $color-gray-600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing-04;

  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  .deleteButton:hover & {
    background: $color-white;
    color: $color-gray-800;
  }

  .deleteButton:active & {
    transform: scale(0.95);
  }
}

.deleteButtonIcon {
  width: 16px;
  height: 16px;
}
</style>
